import React, { useEffect, useState } from "react"
import { XMarkIcon, HomeIcon, BuildingStorefrontIcon, ChevronRightIcon, ChevronDownIcon, UserIcon, ArrowLeftOnRectangleIcon } from "@heroicons/react/24/outline"
import { usePageContext } from "vike-react/usePageContext"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Value from "aldoo-ra/Value"
import { twMerge } from "tailwind-merge"
import { AnimatePresence, motion } from "framer-motion"
import Logo from "../Logo"
import LanguageSwitcher from "./LanguageSwitcher"
import { ProfileMenuContent } from "./ProfileButton"
import Button from "aldoo-ra/Button"
import useT from "../Storefront/hooks/useT"
import { useCategories, useContentTypes } from "../Storefront/hooks/useCatalogData"
import { buildCategoryUrl, childCategories, rootCategories } from "../Storefront/utils/categories"
import { requestSignIn } from "../Storefront/utils/signIn"
import UserAvatar from "../Storefront/ui/UserAvatar"
import { Thumb } from "../AdminShared/ui"

const itemCls = "flex w-full items-center justify-between px-5 py-[15px] text-left text-[16px] text-[#0F1111] hover:bg-black/5"
const headingCls = "block px-5 py-3 text-[18px] font-bold text-[#0F1111]"

function CategoryItem({ category, all, onNavigate }) {
  const [expanded, setExpanded] = useState(false)
  const subs = childCategories(all, category.categoryId)
  if (subs.length === 0) {
    return (
      <Button variant="none" href={buildCategoryUrl(category.categoryId, all)} onClick={onNavigate} className={itemCls}>
        {category.name}
      </Button>
    )
  }
  return (
    <>
      <button type="button" onClick={() => setExpanded((e) => !e)} className={itemCls} aria-expanded={expanded}>
        <span className="flex-1">{category.name}</span>
        {expanded ? <ChevronDownIcon className="w-6 h-6 mr-2" /> : <ChevronRightIcon className="w-6 h-6 mr-2" />}
      </button>
      {expanded && (
        <div className="flex flex-col bg-[#f5f5f5] py-2">
          <Button variant="none" href={buildCategoryUrl(category.categoryId, all)} onClick={onNavigate} className={twMerge(itemCls, "pl-9 font-semibold")}>
            {category.name}
          </Button>
          {subs.map((sub) => (
            <Button variant="none" key={sub.categoryId} href={buildCategoryUrl(sub.categoryId, all)} onClick={onNavigate} className={twMerge(itemCls, "pl-9")}>
              {sub.name}
            </Button>
          ))}
        </div>
      )}
    </>
  )
}

/**
 * The merchant portal's sections (Vendor/index.jsx hands them over while it is open): the drawer's
 * menu there, in place of the store's categories — each opens its section, the open one marked.
 */
function PortalSections({ menu, onClose, t }) {
  const pick = (id) => {
    onClose?.()
    menu.select?.(id)
  }
  return (
    <div className="py-2" data-testid="mobile-portal-menu">
      {/* the brand stays as it is, in every language */}
      <span className={headingCls}>Aldoo {t`Merchant Portal`}</span>
      {menu.items.map((item) => {
        const active = item.id === menu.current
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => pick(item.id)}
            className={twMerge(itemCls, "justify-start gap-4", active && "bg-primary/10 font-semibold text-primary")}
            aria-current={active ? "page" : undefined}
            data-testid={`mobile-portal-${item.id}`}
          >
            <span className="w-6 h-6 shrink-0 [&>svg]:w-6 [&>svg]:h-6">{item.icon}</span>
            <span className="flex-1">{item.label}</span>
            {item.badge && <span className={twMerge("min-w-[22px] h-[22px] px-1.5 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center", item.badgeClassName)}>{item.badge}</span>}
          </button>
        )
      })}
      <button type="button" onClick={() => (onClose?.(), menu.signOut?.())} className={twMerge(itemCls, "justify-start gap-4")} data-testid="mobile-portal-signout">
        <ArrowLeftOnRectangleIcon className="w-6 h-6 shrink-0" />
        <span className="flex-1">{t`Sign Out`}</span>
      </button>
    </div>
  )
}

/**
 * Hamburger drawer (<md): sign-in / profile, home, categories with
 * expandable subcategories, content types, language. In the merchant portal: its sections instead
 * of the store's categories, content types and account menu.
 */
export default function MobileMenu({ open, onClose }) {
  const t = useT()
  const pageContext = usePageContext()
  const [auth] = PlatformAuth.useAuth()
  const [profile] = Value("user-profile")
  const { list: categories } = useCategories()
  const { list: contentTypes } = useContentTypes()
  // the merchant portal open: its sections are the menu
  const [portalMenu] = Value("vendor-portal-menu")
  const inPortal = !!portalMenu?.items?.length

  // Close on navigation
  useEffect(() => {
    onClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageContext.urlPathname])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === "Escape" && onClose?.()
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [open, onClose])

  const roots = rootCategories(categories)
  const types = [...contentTypes]
    .filter((ct) => ct.productCount === undefined || ct.productCount > 0)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || (a.name || "").localeCompare(b.name || ""))

  // opening: the shade fades in and the panel slides in from the left; closing: the other way round
  const slide = { type: "tween", ease: [0.32, 0.72, 0, 1], duration: 0.28 }
  return (
    <AnimatePresence>
    {open && (
    <motion.div key="mobile-menu" className="fixed inset-0 z-[1400] lg:hidden" role="dialog" aria-modal="true" initial={{ opacity: 1 }} exit={{ opacity: 1 }}>
      <motion.div className="absolute inset-0 bg-black/50" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} />
      <motion.button
        type="button"
        onClick={onClose}
        aria-label={t`Close`}
        className="fixed top-5 left-[calc(90vw-22px)] z-[1401] flex h-11 w-11 items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.2 }}
      >
        <XMarkIcon className="w-8 h-8" />
      </motion.button>

      <motion.nav className="absolute left-0 top-0 h-full w-[85vw] max-w-[360px] overflow-y-auto overflow-x-hidden bg-paper pb-24 shadow-2xl" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={slide}>
        <div className="bg-dark p-5 pb-4 text-white">
          <div className="mb-4 flex justify-end">
            {auth ? (
              <Button variant="none" href="/user" className="flex items-center gap-2 rounded px-3 py-2 text-[16px] font-semibold hover:bg-white/10">
                <UserAvatar user={profile} size={28} />
                {profile?.name || t`User`}
              </Button>
            ) : (
              <Button variant="none" onClick={() => { onClose?.(); requestSignIn() }} className="flex items-center gap-1 rounded px-3 py-2 text-[16px] font-semibold hover:bg-white/10">
                <UserIcon className="w-7 h-7" />
                {t`Sign In`}
              </Button>
            )}
          </div>
          {/* in the merchant portal: the organization it is open for, in place of "Browse Aldoo" */}
          {inPortal ? (
            <div className="flex items-center gap-3 min-w-0" data-testid="mobile-portal-org">
              <Thumb src={portalMenu.org?.logo} name={portalMenu.org?.name || "?"} className="w-12 h-12 rounded-full text-base shrink-0 bg-white text-dark" />
              <div className="flex flex-col items-start min-w-0">
                <span className="text-[18px] font-bold leading-tight line-clamp-2">{portalMenu.org?.name}</span>
                {/* another of this person's organizations (the switch the portal's header had) */}
                {portalMenu.switchOrg && (
                  <button type="button" onClick={() => (onClose?.(), portalMenu.switchOrg())} className="mt-1 text-[14px] text-white/80 underline hover:text-white" data-testid="mobile-portal-switch-org">
                    {t`Switch organization`}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className="m-0 mb-1 text-[14px] font-semibold">{t`Browse`}</p>
              <Logo />
            </>
          )}
        </div>

        {inPortal && (
          <>
            <PortalSections menu={portalMenu} onClose={onClose} t={t} />
            <hr className="m-0 border-b-2 border-bordered" />
          </>
        )}

        <div className="py-2">
          <Button variant="none" href="/" className={twMerge(itemCls, "py-3 text-[18px] font-bold")}>
            {t`Aldoo Home`}
            <HomeIcon className="w-5 h-5 mr-3" />
          </Button>
          {/* the other side of the counter, as the desktop's secondary bar has it (in the portal: its sections above) */}
          {!inPortal && (
            <Button variant="none" href="/vendor" className={twMerge(itemCls, "py-3 text-[18px] font-bold")} data-testid="mobile-merchant-portal-link">
              {t`Merchant Portal`}
              <BuildingStorefrontIcon className="w-5 h-5 mr-3" />
            </Button>
          )}
        </div>
        <hr className="m-0 border-b-2 border-bordered" />

        {!inPortal && (
        <>
        <div className="py-2">
          <Button variant="none" href="/categories" className={headingCls}>
            {t`Categories`}
          </Button>
          {roots.map((category) => (
            <CategoryItem key={category.categoryId} category={category} all={categories} onNavigate={onClose} />
          ))}
        </div>
        <hr className="m-0 border-b-2 border-bordered" />

        {types.length > 0 && (
          <>
            <div className="py-2">
              <span className={headingCls}>{t`Content Types`}</span>
              {types.map((ct) => (
                <Button variant="none" key={ct.contentId} href={`/type/${ct.contentId}`} className={itemCls}>
                  {ct.name}
                </Button>
              ))}
            </div>
            <hr className="m-0 border-b-2 border-bordered" />
          </>
        )}

        </>
        )}

        <div className="flex flex-col gap-4 bg-dark p-5">
          <span className="text-[14px] font-semibold text-white">{t`Select Language`}</span>
          <LanguageSwitcher variant="select" />
        </div>

        {auth && !inPortal && (
          <div className="px-3">
            <ProfileMenuContent close={onClose} />
          </div>
        )}
      </motion.nav>
    </motion.div>
    )}
    </AnimatePresence>
  )
}
